// How a time reads to a patient.
//
// The page rendered the slot string straight from the API — "13:00" — because
// that is how the availability service speaks. Those are the clinic's real
// hours and always were (Dr. Levitan works 10:00–12:00 and 13:00–16:00), so
// this is a formatting problem and not a timezone one. Worth stating plainly:
// "correcting" 13:00 by shifting it would move every appointment five hours and
// break real bookings.
//
// Nothing here consults a user preference. The app has one — `dateTimeFormat` —
// but it lives on a logged-in user, and the person reading this page is a
// patient with no account. A US practice writing to its own patients gets
// 12-hour with AM/PM.
import { formatClockString } from '../utils/dateTime';

// "13:00" -> "1:00 PM". The rule lives in utils/dateTime beside the other
// formatters, because the staff panel needs exactly the same one — its slot
// labels had the same 24-hour bug, in the chips and in the message the office
// texts the patient. No preference is passed: the person reading this page has
// no account and so no preference, and a US practice writing to its own patients
// gets AM/PM.
export function patientTimeLabel(hhmm: string): string {
  return formatClockString(hhmm);
}

// "2026-09-14" -> "Monday, 14 September". Built from the parts rather than
// `new Date(iso)`, which reads a bare date as UTC and lands a day early
// anywhere west of Greenwich — the patient would be told the wrong weekday.
const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

export function patientDayLabel(iso: string): string {
  const m = String(iso || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return String(iso || '');
  const year = Number(m[1]);
  const month = Number(m[2]);
  const day = Number(m[3]);
  const weekday = DAYS[new Date(Date.UTC(year, month - 1, day)).getUTCDay()];
  return `${weekday}, ${day} ${MONTHS[month - 1]}`;
}

// The single line the patient confirms against, so everything they are agreeing
// to is in one sentence rather than spread across the page.
export function patientBookingSummary(
  iso: string,
  hhmm: string,
  durationMinutes?: number,
  providerName?: string,
): string {
  const when = `${patientDayLabel(iso)} at ${patientTimeLabel(hhmm)}`;
  const mins = durationMinutes ? `, ${durationMinutes} minutes` : '';
  const who = providerName ? ` with ${providerName}` : '';
  return `${when}${mins}${who}`;
}
